// 3-layer concept explainer — triggered when child gets 2+ wrong on same topic.
// Layer 0 → analogy, Layer 1 → step-by-step method, Layer 2 → memory hook → story resume.

import { useState, useEffect } from 'react';
import { triggerExplainer } from '../api/client';
import type { ConceptExplanation } from '../api/types';
import AdventurerIllustration from '../components/adventurer/AdventurerIllustration';
import type { AdventurerId } from '../components/adventurer/AdventurerStamp';
import { ADVENTURERS } from '../components/adventurer/AdventurerStamp';
import { useTTS } from '../hooks/useTTS';

type Layer = 0 | 1 | 2 | 'resume';

interface Props {
  topicKey: string;
  topicLabel?: string;
  adventurerId: AdventurerId;
  onDone: () => void;
}

const HOOK_ICONS: Record<string, string> = {
  rhyme: '🎵',
  finger_trick: '✋',
  visual_rule: '👁',
  mnemonic: '💡',
};

export default function ExplainerScreen({ topicKey, topicLabel, adventurerId, onDone }: Props) {
  const adv = ADVENTURERS[adventurerId];
  const [explanation, setExplanation] = useState<ConceptExplanation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [layer, setLayer] = useState<Layer>(0);
  const { enabled: ttsEnabled, available: ttsAvailable, speak: ttsSpeak, stop: ttsStop } = useTTS();

  useEffect(() => {
    setLoading(true);
    triggerExplainer(topicKey)
      .then(({ explanation: exp }) => { setExplanation(exp); setLoading(false); })
      .catch(e => { setError(String(e)); setLoading(false); });
  }, [topicKey]);

  // Auto-speak the current layer's content for pre-fluent K1–K2 readers.
  // Honours the global TTS toggle (localStorage); stops any in-flight utterance on layer change.
  useEffect(() => {
    if (!ttsEnabled || !ttsAvailable || !explanation) return;
    const { layer1, layer2, layer3, story_resume_text } = explanation;
    let text = '';
    if (layer === 0) {
      text = [layer1.narrative_setup, layer1.analogy_text].filter(Boolean).join(' ');
    } else if (layer === 1) {
      text = [
        layer2.step_by_step.join('. '),
        layer2.worked_problem && `Beispiel: ${layer2.worked_problem}`,
        layer2.worked_answer && `Ergebnis ${layer2.worked_answer}`,
      ].filter(Boolean).join('. ');
    } else if (layer === 2) {
      text = [layer3.hook_text, layer3.hook_explanation].filter(Boolean).join(' ');
    } else {
      text = story_resume_text;
    }
    if (text) ttsSpeak(text);
    return () => ttsStop();
  }, [layer, explanation, ttsEnabled, ttsAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  const monoLabel: React.CSSProperties = {
    fontFamily: 'var(--mono)',
    fontSize: 10, letterSpacing: '0.2em',
    color: 'var(--ink-muted)', textTransform: 'uppercase',
    display: 'block', marginBottom: 8,
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex', height: '100dvh',
        alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 20,
        background: 'var(--parchment)', fontFamily: 'var(--sans)',
      }}>
        <AdventurerIllustration id={adventurerId} pose="thinking" size={96} />
        <div style={{ fontFamily: 'var(--serif)', fontSize: 20, fontStyle: 'italic', color: 'var(--ink-muted)' }}>
          {adv.name} überlegt…
        </div>
      </div>
    );
  }

  if (error || !explanation) {
    return (
      <div style={{
        display: 'flex', height: '100dvh',
        alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 16,
        background: 'var(--parchment)',
      }}>
        <div style={{ fontFamily: 'var(--display)', fontSize: 24, color: 'var(--wrong)' }}>Ups — da ist etwas schiefgelaufen.</div>
        <button onClick={onDone} style={{
          padding: '12px 24px', background: 'var(--amber)', color: '#fff',
          border: 'none', borderRadius: 3, fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 700, cursor: 'pointer',
        }}>Zurück zur Geschichte →</button>
      </div>
    );
  }

  const { layer1, layer2, layer3, story_resume_text } = explanation;

  return (
    <div style={{
      display: 'flex', minHeight: '100dvh',
      alignItems: 'flex-start', justifyContent: 'center',
      background: 'var(--parchment)',
      fontFamily: 'var(--sans)',
      overflowY: 'auto',
      padding: '48px 24px 80px',
    }}>
      <div style={{ width: '100%', maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 28 }}>

        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <AdventurerIllustration id={adventurerId} beat="reading" size={72} />
          <div>
            <div style={monoLabel}>{topicLabel ?? topicKey}</div>
            <div style={{
              fontFamily: 'var(--display)',
              fontSize: 32, fontWeight: 600,
              color: 'var(--ink)', letterSpacing: '-0.02em', lineHeight: 1.1,
            }}>
              {layer === 'resume' ? 'Gut gemacht!' : 'Finn erklärt'}
            </div>
          </div>
        </div>

        {/* layer progress pills */}
        <div style={{ display: 'flex', gap: 6 }}>
          {(['Erklärung','Methode','Trick'] as const).map((label, i) => {
            const isDone = (layer === 'resume') || (typeof layer === 'number' && layer > i);
            const isCur = typeof layer === 'number' && layer === i;
            return (
              <div key={label} style={{
                flex: 1, padding: '6px 0',
                background: isDone ? 'var(--ink)' : isCur ? 'var(--amber)' : 'rgba(60,50,40,0.08)',
                borderRadius: 20,
                fontFamily: 'var(--mono)',
                fontSize: 9, letterSpacing: '0.18em',
                color: (isDone || isCur) ? '#fff' : 'var(--ink-muted)',
                textTransform: 'uppercase',
                textAlign: 'center',
                transition: 'all 0.2s',
              }}>{label}</div>
            );
          })}
        </div>

        {/* ── Layer 0 — Analogy ── */}
        {layer === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, animation: 'fade-in-up 0.35s ease-out' }}>
            <div style={{
              background: 'var(--card)',
              border: '1px solid rgba(60,50,40,0.1)',
              borderRadius: 4,
              padding: '22px 24px',
            }}>
              <span style={monoLabel}>Finn erzählt</span>
              <div style={{
                fontFamily: 'var(--serif)',
                fontSize: 19, lineHeight: 1.6,
                color: 'var(--ink)', fontStyle: 'italic',
              }}>
                <span style={{
                  float: 'left',
                  fontFamily: 'var(--display)',
                  fontSize: 56, lineHeight: 0.85, fontWeight: 600,
                  color: 'var(--amber-deep)',
                  marginRight: 8, marginTop: 4, marginBottom: -4,
                }}>{layer1.narrative_setup.charAt(0)}</span>
                <span>{layer1.narrative_setup.slice(1)}</span>
              </div>
            </div>

            <div style={{
              background: 'oklch(0.97 0.025 100)',
              border: '1px solid oklch(0.88 0.05 90)',
              borderRadius: 4,
              padding: '20px 24px',
            }}>
              <span style={monoLabel}>Die Idee dahinter</span>
              <div style={{
                fontFamily: 'var(--sans)', fontSize: 16, lineHeight: 1.6, color: 'var(--ink)',
              }}>{layer1.analogy_text}</div>
            </div>

            <button onClick={() => setLayer(1)} style={{
              padding: '14px 28px',
              background: 'var(--amber)', color: '#fff', border: 'none',
              borderRadius: 3, fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 700,
              cursor: 'pointer', boxShadow: '0 4px 12px rgba(201,106,47,0.3)',
              alignSelf: 'flex-end',
            }}>Ich hab's verstanden! →</button>
          </div>
        )}

        {/* ── Layer 1 — Method ── */}
        {layer === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, animation: 'fade-in-up 0.35s ease-out' }}>
            <div style={{
              background: 'var(--card)',
              border: '1px solid rgba(60,50,40,0.1)',
              borderRadius: 4,
              padding: '22px 24px',
              display: 'flex', flexDirection: 'column', gap: 12,
            }}>
              <span style={monoLabel}>Schritt für Schritt</span>
              {layer2.step_by_step.map((step, i) => (
                <div key={i} style={{
                  display: 'flex', gap: 14, alignItems: 'flex-start',
                }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%',
                    background: 'var(--amber)', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'var(--display)', fontSize: 14, fontWeight: 600,
                    flexShrink: 0, marginTop: 1,
                  }}>{i + 1}</div>
                  <div style={{ fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.55, color: 'var(--ink)' }}>{step}</div>
                </div>
              ))}
            </div>

            {/* worked example */}
            <div style={{
              background: 'oklch(0.97 0.025 100)',
              border: '1px solid oklch(0.88 0.05 90)',
              borderRadius: 4,
              padding: '20px 24px',
              display: 'flex', flexDirection: 'column', gap: 12,
            }}>
              <span style={monoLabel}>Beispiel: {layer2.worked_problem}</span>
              {layer2.worked_steps.map((step, i) => (
                <div key={i} style={{
                  fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--ink)',
                  paddingLeft: 16, borderLeft: '2px solid var(--amber)',
                }}>{step}</div>
              ))}
              <div style={{
                fontFamily: 'var(--display)',
                fontSize: 22, fontWeight: 600,
                color: 'var(--correct)',
                marginTop: 4,
              }}>= {layer2.worked_answer}</div>
            </div>

            <button onClick={() => setLayer(2)} style={{
              padding: '14px 28px',
              background: 'var(--amber)', color: '#fff', border: 'none',
              borderRadius: 3, fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 700,
              cursor: 'pointer', boxShadow: '0 4px 12px rgba(201,106,47,0.3)',
              alignSelf: 'flex-end',
            }}>Weiter →</button>
          </div>
        )}

        {/* ── Layer 2 — Memory hook ── */}
        {layer === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, animation: 'fade-in-up 0.35s ease-out' }}>
            <div style={{
              background: 'var(--card)',
              border: '1px solid rgba(60,50,40,0.1)',
              borderRadius: 4,
              padding: '22px 24px',
              display: 'flex', flexDirection: 'column', gap: 14,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: 'var(--amber-soft)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, flexShrink: 0,
                }}>{HOOK_ICONS[layer3.hook_type] ?? '💡'}</div>
                <span style={monoLabel}>{layer3.hook_type.replace(/_/g,' ')}</span>
              </div>

              <div style={{
                fontFamily: 'var(--display)',
                fontSize: 26, fontWeight: 600,
                color: 'var(--ink)', lineHeight: 1.3,
              }}>{layer3.hook_text}</div>

              <div style={{
                fontFamily: 'var(--serif)',
                fontSize: 15, fontStyle: 'italic',
                color: 'var(--ink-soft)', lineHeight: 1.55,
                borderTop: '1px dashed rgba(60,50,40,0.12)',
                paddingTop: 14,
              }}>{layer3.hook_explanation}</div>
            </div>

            <button onClick={() => setLayer('resume')} style={{
              padding: '14px 28px',
              background: 'var(--amber)', color: '#fff', border: 'none',
              borderRadius: 3, fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 700,
              cursor: 'pointer', boxShadow: '0 4px 12px rgba(201,106,47,0.3)',
              alignSelf: 'flex-end',
            }}>Weiter geht's! →</button>
          </div>
        )}

        {/* ── Resume — story transition ── */}
        {layer === 'resume' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, animation: 'fade-in-up 0.35s ease-out' }}>
            <div style={{
              background: 'var(--card)',
              border: '1px solid rgba(60,50,40,0.1)',
              borderRadius: 4,
              padding: '22px 24px',
              display: 'flex', gap: 16, alignItems: 'flex-start',
            }}>
              <AdventurerIllustration id={adventurerId} pose="encouraging" size={56} />
              <div style={{
                fontFamily: 'var(--serif)',
                fontSize: 17, fontStyle: 'italic',
                color: 'var(--ink)', lineHeight: 1.6,
              }}>{story_resume_text}</div>
            </div>

            <div style={{
              padding: '14px 18px',
              background: 'var(--correct-soft)',
              border: '1px solid var(--correct)',
              borderRadius: 3,
              fontFamily: 'var(--mono)',
              fontSize: 10, letterSpacing: '0.2em',
              color: 'var(--correct)', textTransform: 'uppercase',
            }}>
              2 Übungsaufgaben zu diesem Thema folgen
            </div>

            <button onClick={onDone} style={{
              padding: '16px 32px',
              background: 'var(--ink)', color: 'var(--parchment)', border: 'none',
              borderRadius: 3, fontFamily: 'var(--sans)', fontSize: 16, fontWeight: 700,
              cursor: 'pointer', alignSelf: 'center',
              letterSpacing: '0.02em',
            }}>Zurück zur Geschichte →</button>
          </div>
        )}
      </div>
    </div>
  );
}
